import React from 'react';
import { Link } from '@material-ui/core';
import { Typography } from '@cbreone/core-ui';
import DOMPurify from 'dompurify';
import { linkAttributes, linkAttributes_pac, PropertyTypes, SpecificationAmenitiesAttributes, CE_LINK_COUNTRIES } from '../data/constants';
import { CountriesWithPAC } from '../map/constants/MapStyles';
import i18n from '../i18n';

interface RenderLinkFieldValueOptions {
  value: string | number | null | undefined;
  fieldLabel: string;
  surveyCountry: string;
  propertyType?: string;
}

/**
 * Detects if a URL is a map/directions link
 * @param url - The URL to check
 * @returns true if the URL is a map link, false otherwise
 */
const isMapLink = (url: string): boolean => {
  const mapPatterns = [
    /google\.com\/maps/i,
    /maps\.google/i,
    /bing\.com\/maps/i,
    /maps\.bing/i,
    /apple\.com\/maps/i,
    /maps\.apple/i,
    /mapquest\.com/i,
    /waze\.com/i,
    /openstreetmap\.org/i,
    /here\.com/i,
    /maps\.here/i,
    /yandex\.com\/maps/i,
    /maps\.yandex/i,
  ];

  return mapPatterns.some((pattern) => pattern.test(url));
};

/**
 * Renders a field value with appropriate formatting based on the field type and content.
 * Handles link fields and regular text fields with proper encoding and formatting.
 * Supports various URL types including map links with query parameters.
 * Map links are displayed with "Click for Directions" text for better UX.
 *
 * @param options - Configuration object containing:
 *   - value: The field value to render
 *   - fieldLabel: The label of the field (used to determine if it's a link field)
 *   - surveyCountry: The country code to determine if it's a PAC country survey
 *   - propertyType: The type of property (optional, used for Industrial properties in Spain and Germany)
 *
 * @returns JSX element with the rendered field value
 *
 * @example
 * // Map links like: https://www.google.com/maps?q=49.981336,20.279979
 * // Display as: "Click for Directions"
 * // Regular URLs display the full URL
 */
const renderLinkFieldValue = ({
  value,
  fieldLabel,
  surveyCountry,
  propertyType = ''
}: RenderLinkFieldValueOptions): React.ReactElement => {
  const surveyCountry_pac = CountriesWithPAC.includes(surveyCountry);
  const isLinkField = (surveyCountry_pac && linkAttributes_pac.includes(fieldLabel)) ||
    (!surveyCountry_pac && linkAttributes.includes(fieldLabel));
  const isCELinkCountry = CE_LINK_COUNTRIES.includes(surveyCountry);
  const normalizedValue = value === undefined || value === null ? '' : String(value);
  const hasValue = normalizedValue.trim() !== '';

  if (isCELinkCountry && isLinkField && hasValue) {
    // For Spain and Germany link fields, parse the format: "DOSSIER - https://www.cbre.com, \n WEBSITE - https://www.cbre.es/officino"
    // and render as Link components. Also handle standalone URLs without labels.
    const lines = normalizedValue.split(/,\s*\n|\n/).map((line) => line.trim()).filter((line) => line.length > 0);

    return (
      <>
        {lines.map((line: string, idx: number) => {
          // First, check if it's a URL without label (standalone URL)
          const urlPattern = /^https?:\/\/|^www\.|^[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
          if (urlPattern.test(line)) {
            let safeUrl = line;

            // Add protocol if missing
            if (!safeUrl.match(/^https?:\/\//i)) {
              safeUrl = `https://${safeUrl}`;
            }

            return (
              <React.Fragment key={`spain-url-${idx}`}>
                <Link
                  href={safeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    wordWrap: 'break-word',
                    fontSize: '16px',
                  }}
                >
                  {safeUrl}
                </Link>
                <br />
              </React.Fragment>
            );
          }
          // Then, check for "LABEL - URL" format with spaces around the dash
          const match = line.match(/^(.+?)\s+-\s+(.+)$/);
          if (match) {
            // Format: "LABEL - URL"
            const [, label, url] = match;
            const trimmedLabel = label.trim();
            let safeUrl = url.trim();

            // Add protocol if missing
            if (safeUrl && !safeUrl.match(/^https?:\/\//i)) {
              safeUrl = `https://${safeUrl}`;
            }

            return (
              <React.Fragment key={`spain-link-${idx}`}>
                <Link
                  href={safeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    wordWrap: 'break-word',
                    fontSize: '16px',
                  }}
                >
                  {trimmedLabel || safeUrl}
                </Link>
                <br />
              </React.Fragment>
            );
          }
          // Ignore simple text without URLs (like "lease link", "video link")
          return null;
        })}
      </>
    );
  }

  if (isLinkField && hasValue) {
    // Split the string into individual URLs more intelligently
    // Look for URL patterns and extract them, preserving commas within URLs (like coordinates)
    // Pattern matches URLs and stops at comma+space or whitespace (not comma alone)
    const urlPattern = /https?:\/\/[^\s]+/g;
    const matches = normalizedValue.match(urlPattern);
    let urls: string[];
    if (matches && matches.length > 0) {
      // If we found URL patterns, use them
      urls = matches.map((url) => url.trim());
    } else {
      // Fallback: split on comma+space or newline, then filter out empty strings
      // This handles URLs without protocol like "google.com, www.cbre.com"
      urls = normalizedValue
        .split(/,\s+|\n+/)
        .map((url) => url.trim())
        .filter((url) => url.length > 0);
    }

    if (urls.length) {
      return (
        <>
          {urls.map((url: string, idx: number) => {
            let safeUrl = url.trim?.();

            // Add protocol if missing
            if (safeUrl && !safeUrl.match(/^https?:\/\//i)) {
              safeUrl = `https://${safeUrl}`;
            }

            // For URLs that are already properly formatted (like map links),
            // only encode if necessary to avoid double-encoding
            try {
              // Test if the URL is already valid by attempting to parse it
              const testUrl = new URL(safeUrl);
              // If URL is valid, use it as-is to preserve query parameters
              safeUrl = testUrl.href;
            } catch {
              // If URL is invalid, try encoding it
              safeUrl = encodeURI(safeUrl);
            }

            return (
              <React.Fragment key={`field-link-${idx}`}>
                <Link
                  href={safeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    wordWrap: 'break-word',
                    fontSize: '16px',
                  }}
                >
                  {isMapLink(url.trim?.()) ? i18n.t('common.clickForDirections') : url.trim?.()}
                </Link>
                <br />
              </React.Fragment>
            );
          })}
        </>
      );
    }
    return <Typography>-</Typography>;
  }

  if (hasValue) {
    // eslint-disable-next-line max-len
    const isIndustrialCECountry = propertyType.toLowerCase() === PropertyTypes.Industrial.toLowerCase() && CE_LINK_COUNTRIES.includes(surveyCountry);
    const isSpecificationField = SpecificationAmenitiesAttributes.includes(fieldLabel);

    if (isIndustrialCECountry && isSpecificationField) {
      const sanitizedHtml = DOMPurify.sanitize(normalizedValue.trim(), { USE_PROFILES: { html: true } });
      return <Typography variant="caption" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />;
    }

    const containsHtml = /<[a-z][\s\S]*>/i.test(normalizedValue);
    if (containsHtml) {
      const normalizedHtml = normalizedValue.trim().replace(/<p>\s*<br\s*\/?\>\s*<\/p>/gi, '');
      const sanitizedHtml = DOMPurify.sanitize(normalizedHtml, { USE_PROFILES: { html: true } });
      return (
        <Typography
          dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
        />
      );
    }

    return <Typography>{normalizedValue.trim()}</Typography>;
  }

  return <Typography>-</Typography>;
};

export default renderLinkFieldValue;
